import {
  cancel,
  fork,
  take,
  put,
  call,
  takeLatest,
  select,
  takeEvery,
} from 'redux-saga/effects'
import { DateTime } from 'luxon'
import { capitalize, isEmpty, debounce } from 'lodash-es'
import { reset as resetForm } from 'redux-form/dist/redux-form'

import { showNotice } from '../../../Layout/Notices/Notices'

import {
  fetchComments,
  fetchComment,
  addComment,
  deleteComment,
  updateComment,
  postCommentSetOrder,
} from './api'

const debouncedSetQuestionsOrder = debounce(postCommentSetOrder, 1000)

// eslint-disable-next-line import-x/no-default-export
export default ({ actionNames, props, getFeatureRoot }) => {
  const { commentType, order } = props

  const getComments = (state) => getFeatureRoot(state).questions.comments
  const getWebinarsCurrentPage = (state) =>
    getFeatureRoot(state).questions.currentPage
  const getWatchingRealtimeStatus = (state) =>
    getFeatureRoot(state).questions.isWatchingRealtime
  const getPresenterToken = (state) =>
    getFeatureRoot(state).questions.presenterToken
  const getActions = (isInfiniteLoad) => {
    if (isInfiniteLoad) {
      return {
        success: actionNames.INFINITE_LOAD_QUESTIONS_SUCCESS,
        failure: actionNames.INFINITE_LOAD_QUESTIONS_FAILURE,
      }
    }

    return {
      success: actionNames.FETCH_QUESTIONS_SUCCESS,
      failure: actionNames.FETCH_QUESTIONS_FAILURE,
    }
  }

  function* fetchCommentsWorker({ liveId, page = 1, isInfiniteLoad = false }) {
    const { isGroupedByStatus } = props
    const actions = getActions(isInfiniteLoad)
    const presenterToken = yield select(getPresenterToken)
    let size = 40

    // if the questions are grouped by status there is no pagination, setting a
    // high size to not limit the number of questions.
    if (isGroupedByStatus) {
      size = 5000
    }

    try {
      const { comments, total_count: totalCount } = yield call(
        fetchComments,
        liveId,
        commentType,
        { presenterToken, order, size },
        page
      )
      yield put({
        type: actions.success,
        comments,
        totalCount,
        page,
      })
    } catch (err) {
      showNotice({
        message: `${capitalize(commentType)} list could not be loaded`,
        expirationMillis: 5e3,
        isClosable: true,
      })
      yield put({ type: actions.failure })
    }
  }

  function* fetchSingleCommentWorker({ commentId, liveId }) {
    const presenterToken = yield select(getPresenterToken)

    try {
      const { comment } = yield call(
        fetchComment,
        commentId,
        liveId,
        presenterToken
      )

      // questions can be created anonymously and
      // then are not allowed to be accessed anonymously,
      // so we will only list them
      if (isEmpty(comment)) {
        return
      }

      yield put({ type: actionNames.FETCH_SINGLE_QUESTION_SUCCESS, comment })
    } catch (err) {
      showNotice({
        message: `${capitalize(commentType)} message could not be loaded`,
        expirationMillis: 5000,
        isClosable: true,
      })
    }
  }

  function* optimisticallyAddCommentWorker(content, commentId, name) {
    const fullName = name || props.localParticipantName

    if (isEmpty(fullName)) {
      yield
      return
    }

    const xHtmlTags = /<\/?\w+[^>]*\/?>/gi

    const comment = {
      content_text: content.replace(xHtmlTags, ''),
      comment_id: commentId,
      entry_date_ansi: DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss'),
      name: fullName,
    }

    yield put({ type: actionNames.OPTIMISTICALLY_ADD_QUESTION, comment })
  }

  function* addCommentWorker({ values, formSubmitPromise }) {
    const { resolve, reject } = formSubmitPromise
    const { name, content } = values
    const resetAction = actionNames.RESET_ADD_QUESTION_BUTTON_STATE
    const failureAction = actionNames.ADD_QUESTION_FAILURE

    try {
      const { liveId, token, uuid } = props

      const userInfo = { name, uuid }
      yield put(resetForm(`${commentType}Form`))
      yield put({ type: resetAction })
      resolve()

      const { comment } = yield call(
        addComment,
        liveId,
        token,
        content,
        commentType,
        userInfo
      )

      yield call(
        optimisticallyAddCommentWorker,
        content,
        comment.comment_id,
        name
      )
      yield call(fetchSingleCommentWorker, {
        commentId: comment.comment_id,
        liveId,
      })
    } catch (err) {
      showNotice({
        message: `The ${commentType} list could not be loaded`,
        expirationMillis: 5000,
        isClosable: true,
      })
      yield put({ type: failureAction })
      reject()
    }
  }

  function* watchAdd() {
    yield takeEvery(actionNames.ADD_QUESTION, addCommentWorker)
  }

  function* deleteCommentWorker({ commentId }) {
    try {
      yield call(deleteComment, props.liveId, commentId)
    } catch (err) {
      showNotice({
        message: 'The item could not be deleted',
        expirationMillis: 5000,
        isClosable: true,
      })
    }
  }

  function* watchDelete() {
    yield takeLatest(actionNames.DELETE_QUESTION, deleteCommentWorker)
  }

  function* moveCommentWorker({ fromIndex, toIndex }) {
    try {
      const comments = yield select(getComments)
      const commentsList = [...comments.values()]

      let newFromIndex = fromIndex
      let newToIndex = toIndex
      commentsList.forEach((comment, index) => {
        if (comment.comment_status !== '') {
          if (index <= newFromIndex) {
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            newFromIndex += 1
          }

          if (index <= newToIndex) {
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            newToIndex += 1
          }
        }
        return index < newFromIndex || index < newToIndex
      })

      const [comment] = commentsList.splice(newFromIndex, 1)
      commentsList.splice(newToIndex, 0, comment)

      yield put({
        type: actionNames.OPTIMISTICALLY_UPDATE_QUESTION_COMMENTS,
        comments: commentsList,
      })
      yield call(debouncedSetQuestionsOrder, props.liveId, commentsList)
    } catch (err) {
      showNotice({
        message: 'Could not set the order of comments',
        expirationMillis: 5000,
        isClosable: true,
      })
    }
  }

  function* watchMoveComment() {
    yield takeEvery(actionNames.MOVE_QUESTION_COMMENT, moveCommentWorker)
  }

  function* watchInfiniteScroll() {
    yield take(actionNames.FETCH_QUESTIONS_SUCCESS)
    const { liveId } = props

    yield takeLatest(
      actionNames.INFINITE_LOAD_QUESTIONS,
      function* onInfiniteLoad() {
        const currentPage = yield select(getWebinarsCurrentPage)
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const page = currentPage + 1
        yield call(fetchCommentsWorker, { liveId, page, isInfiniteLoad: true })
      }
    )
  }

  function* realtimeOrderCommentsWorker(realtimeData) {
    const newOrder = realtimeData?.payload?.order || []
    const comments = yield select(getComments)
    const orderedComments = newOrder.map((commentId) => comments.get(commentId))

    yield put({
      type: actionNames.OPTIMISTICALLY_UPDATE_QUESTION_COMMENTS,
      comments: orderedComments,
    })
  }

  function* realtimeUpdateCommentWorker(realtimeData) {
    const { comment_id: commentId, comment_status: status } =
      realtimeData?.payload ?? {}
    const comments = yield select(getComments)
    const comment = comments.get(commentId)
    if (comment) {
      comment.comment_status = status

      yield put({
        type: actionNames.OPTIMISTICALLY_UPDATE_QUESTION_COMMENTS,
        comments: [...comments.values()],
      })
    }
  }

  function* watchRealTime() {
    const isWatchingRealtime = yield select(getWatchingRealtimeStatus)

    if (isWatchingRealtime) {
      return
    }

    yield put({ type: actionNames.WATCH_REALTIME_QUESTIONS })

    yield takeEvery(
      actionNames.REALTIME_DATA_UPDATE,
      function* onDataUpdate(action) {
        const { realtimeData } = action

        if (!realtimeData) {
          yield
          return
        }

        if (realtimeData.event === 'add') {
          if (realtimeData.comment_type !== commentType) {
            yield
            return
          }
          yield fork(fetchSingleCommentWorker, {
            commentId: realtimeData.comment_id,
            liveId: realtimeData.object_id,
          })
        } else if (realtimeData.event === 'delete') {
          yield put({
            type: actionNames.REALTIME_REMOVE_COMMENT,
            commentId: realtimeData.comment_id,
          })
        } else if (realtimeData.event === 'comments_order') {
          const realtimeDataCommentType = realtimeData?.payload?.comment_type
          if (
            realtimeDataCommentType &&
            realtimeDataCommentType === commentType
          ) {
            yield fork(realtimeOrderCommentsWorker, realtimeData)
          }
        } else if (realtimeData.event === 'comment_update') {
          yield fork(realtimeUpdateCommentWorker, realtimeData)
        }
      }
    )
  }

  function* watchLeave(action, tasks) {
    yield take(action)
    yield tasks.map((task) => cancel(task))

    yield put({ type: actionNames.RESET_QUESTIONS })
  }

  function* updateCommentWorker({ commentId, status }) {
    const comments = yield select(getComments)
    const comment = comments.get(commentId)
    comment.comment_status = status

    yield put({
      type: actionNames.OPTIMISTICALLY_UPDATE_QUESTION_COMMENTS,
      comments: [...comments.values()],
    })
    yield call(updateComment, props.liveId, commentId, status)
  }

  function* watchUpdateComment() {
    yield takeEvery(actionNames.UPDATE_QUESTION_COMMENT, updateCommentWorker)
  }

  function* init() {
    const tasks = []

    while (true) {
      const action = yield take(actionNames.INIT_QUESTIONS)
      if (!props.doNotFetchQuestions) {
        tasks.push(yield fork(fetchCommentsWorker, action))
      }
      tasks.push(yield fork(watchAdd))
      tasks.push(yield fork(watchDelete))
      tasks.push(yield fork(watchRealTime))
      tasks.push(yield fork(watchInfiniteScroll))
      tasks.push(yield fork(watchMoveComment))
      tasks.push(yield fork(watchUpdateComment))
      yield call(watchLeave, actionNames.LEAVE_QUESTIONS, tasks)
    }
  }

  return {
    default: [init()],
  }
}
